import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberCallout from "@/components/FoundingMemberCallout";

/**
 * Honest comparison: FrazzleCRM vs ActiveCampaign (email-automation
 * switchers). Pricing facts checked 2026-07. Concede AC's real strengths.
 */

const ROWS: [string, string, string][] = [
  ["Pricing model", "Flat plans — contacts included, no bands", "Contact-count matrix: price roughly doubles per band (1k → 2.5k → 5k → 10k…)"],
  ["Entry price", "$0 free forever; paid from $29/mo", "From $15/mo at 1,000 contacts (annual); no free plan"],
  ["At 10,000 contacts", "Same flat plan price", "Pro tier climbs steeply past 5–10k contacts"],
  ["Add-ons", "None — CRM, automations and maps are the product", "CRM pipelines, SMS credits, custom reporting, extra seats billed separately"],
  ["Automation model", "The funnel map IS the automation — one artifact, two-way sync", "Powerful builder, but diagrams and automations live apart"],
  ["Sending", "Bring your own SMTP/Twilio — your domain, provider cost", "Managed sending included (genuinely good deliverability infra)"],
  ["AI / MCP", "Built-in MCP server — your AI agent builds and audits flows", "AI features in-product; no agent-level API"],
  ["Maturity & ecosystem", "Young product, focused surface", "Decade-plus of ML features, 900+ integrations — a real strength"],
];

export default function VsActiveCampaign() {
  return (
    <SiteShell
      title="FrazzleCRM vs ActiveCampaign (2026) — an honest comparison"
      description="FrazzleCRM vs ActiveCampaign, honestly: AC is mature email automation with contact-band pricing that climbs fast. FrazzleCRM is the visual-first alternative — the funnel map IS the automation, flat pricing, bring-your-own sending."
    >

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-12 pt-16 text-center">
          <h1 className="mx-auto max-w-3xl text-[36px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[44px]">
            FrazzleCRM vs ActiveCampaign
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600">
            ActiveCampaign is mature, capable email automation — and its
            contact-band pricing quietly doubles as your list grows.
            FrazzleCRM is the visual-first alternative: flat plans, your
            own sending domain, and a funnel you can actually{" "}
            <em>see</em> running.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
          <table className="w-full min-w-[640px] border-collapse text-left text-[13.5px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-[12.5px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold"> </th>
                <th className="px-4 py-3 font-bold text-indigo-700">FrazzleCRM</th>
                <th className="px-4 py-3 font-semibold">ActiveCampaign</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, us, them]) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3.5 font-medium text-slate-700">{label}</td>
                  <td className="px-4 py-3.5 text-slate-700">{us}</td>
                  <td className="px-4 py-3.5 text-slate-500">{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-[12px] text-slate-400">
          ActiveCampaign pricing as published July 2026; check their site
          for current numbers. Estimate your own costs with our{" "}
          <Link href="/tools/cost-calculator" className="font-medium text-indigo-600 hover:text-indigo-500">
            cost calculator
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose ActiveCampaign if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-600">
              <li>• You want managed sending and don&apos;t want to touch SMTP</li>
              <li>• You lean on predictive/ML features matured over a decade</li>
              <li>• You need their long tail of native integrations</li>
              <li>• Your list is small and stays small (band pricing bites later)</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose FrazzleCRM if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-700">
              <li>• You never want a &ldquo;your list grew, pay double&rdquo; email again</li>
              <li>• You want the funnel diagram to BE the running automation</li>
              <li>• You own deliverability: your domain, your SMTP, at cost</li>
              <li>• You want your AI agent building drafts you approve (MCP)</li>
              <li>• You&apos;d rather start free than start a trial clock</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pt-14">
        <FoundingMemberCallout note="No list-size band to graduate into and no renewal date — your own SMTP keeps sending at provider cost." />
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-[26px] font-bold text-slate-900">
          See your funnel instead of scrolling it.
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-600">
          Free for 500 contacts, no card, no trial clock. Try the{" "}
          <Link href="/tools/funnel-generator" className="font-medium text-indigo-600 hover:text-indigo-500">
            funnel generator
          </Link>{" "}
          without even signing up.
        </p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-6 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free
        </a>
      </section>
    </SiteShell>
  );
}
